import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { AlertTriangle, Truck, Fuel, Calendar, TrendingUp, TrendingDown, Wrench, Target } from 'lucide-react';

const ConstructionFleetDashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [fuelPrice, setFuelPrice] = useState(4.85);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      // Simulate fuel price fluctuation
      if (Math.random() > 0.95) {
        setFuelPrice(prev => prev + (Math.random() - 0.5) * 0.1);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Sample data
  const statsData = [
    { label: 'Active Vehicles', value: 24, color: 'bg-green-500', icon: Truck },
    { label: 'Expiring Soon', value: 6, color: 'bg-yellow-500', icon: Calendar },
    { label: 'Expired Documents', value: 3, color: 'bg-red-500', icon: AlertTriangle },
    { label: 'Monthly Fuel Cost', value: '$8,450', color: 'bg-purple-500', icon: Fuel }
  ];

  const fuelConsumptionData = [
    { day: 'Mon', consumption: 450 },
    { day: 'Tue', consumption: 520 },
    { day: 'Wed', consumption: 480 },
    { day: 'Thu', consumption: 600 },
    { day: 'Fri', consumption: 580 },
    { day: 'Sat', consumption: 320 },
    { day: 'Sun', consumption: 280 }
  ];

  const fuelCostData = [
    { name: 'Excavators', value: 3200, color: '#8b5cf6' },
    { name: 'Dump Trucks', value: 2800, color: '#06b6d4' },
    { name: 'Cranes', value: 1500, color: '#f59e0b' },
    { name: 'Other', value: 950, color: '#ef4444' }
  ];

  const utilizationData = [
    { vehicle: 'Excavators', utilization: 85 },
    { vehicle: 'Dump Trucks', utilization: 78 },
    { vehicle: 'Cranes', utilization: 92 },
    { vehicle: 'Bulldozers', utilization: 65 },
    { vehicle: 'Mixers', utilization: 88 }
  ];

  const efficiencyData = [
    { subject: 'Fuel Efficiency', A: 82, fullMark: 100 },
    { subject: 'Maintenance', A: 75, fullMark: 100 },
    { subject: 'Downtime', A: 88, fullMark: 100 },
    { subject: 'Safety', A: 95, fullMark: 100 },
    { subject: 'Productivity', A: 78, fullMark: 100 }
  ];

  const vehicleExpiryData = [
    { id: 'CV-001', type: 'Excavator', document: 'Registration', expiry: '2025-02-15', status: 'expired' },
    { id: 'CV-012', type: 'Dump Truck', document: 'Insurance', expiry: '2025-08-10', status: 'expiring' },
    { id: 'CV-008', type: 'Crane', document: 'Safety Cert', expiry: '2025-09-22', status: 'active' },
    { id: 'CV-015', type: 'Bulldozer', document: 'Registration', expiry: '2025-01-20', status: 'expired' },
    { id: 'CV-022', type: 'Mixer Truck', document: 'Insurance', expiry: '2025-08-05', status: 'expiring' }
  ];

  const maintenanceData = [
    { vehicle: 'CV-003 Excavator', nextService: 'Aug 5, 2025', hours: '1,250 hrs', priority: 'high' },
    { vehicle: 'CV-007 Dump Truck', nextService: 'Aug 12, 2025', hours: '45,200 mi', priority: 'medium' },
    { vehicle: 'CV-014 Crane', nextService: 'Aug 20, 2025', hours: '890 hrs', priority: 'low' }
  ];

  const projectData = [
    { project: 'Highway Bridge', vehicles: 8, progress: 65, completion: 'Sep 15, 2025' },
    { project: 'Office Complex', vehicles: 6, progress: 40, completion: 'Oct 30, 2025' },
    { project: 'Residential Area', vehicles: 10, progress: 85, completion: 'Aug 20, 2025' }
  ];

  const StatusBadge = ({ status, children }) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800',
      expiring: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-red-100 text-red-800',
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${statusStyles[status]}`}>
        {children}
      </span>
    );
  };

  const StatCard = ({ stat, index }) => {
    const Icon = stat.icon;
    return (
      <div 
        className={`bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-l-4 ${stat.color} animate-fadeInUp`}
        style={{ animationDelay: `${index * 100}ms` }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-3xl font-bold text-gray-800 mb-1">
              {stat.value}
            </div>
            <div className="text-sm text-gray-600 uppercase tracking-wide font-medium">
              {stat.label}
            </div>
          </div>
          <div className={`p-3 rounded-full ${stat.color} bg-opacity-20`}>
            <Icon className={`w-6 h-6 ${stat.color.replace('bg-', 'text-')}`} />
          </div>
        </div>
      </div>
    );
  };

  const ProgressBar = ({ percentage, className = '' }) => (
    <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
      <div 
        className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-1000"
        style={{ width: `${percentage}%` }}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-blue-800 text-white p-8 rounded-t-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-10 transform -skew-x-12 animate-shimmer"></div>
          <div className="relative z-10 text-center">
            <h1 className="text-4xl font-bold mb-2 flex items-center justify-center gap-3">
              <Truck className="w-10 h-10" />
              Construction Fleet Command Center
            </h1>
            <p className="text-lg opacity-90">
              Real-time monitoring and management of construction vehicles and equipment
            </p>
            <div className="text-sm mt-2 opacity-75">
              Last updated: {currentTime.toLocaleTimeString()}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="bg-gray-50 p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsData.map((stat, index) => (
              <StatCard key={stat.label} stat={stat} index={index} />
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white p-8 rounded-b-2xl shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Vehicle Expiry Status */}
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Vehicle Expiry Status
              </h3>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded animate-pulse">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                  <span className="font-semibold text-red-700">
                    Alert: 3 vehicles have expired registrations requiring immediate attention!
                  </span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Vehicle ID</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Type</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Document</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Expiry Date</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicleExpiryData.map((vehicle, index) => (
                      <tr key={vehicle.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-2 font-medium">{vehicle.id}</td>
                        <td className="py-3 px-2">{vehicle.type}</td>
                        <td className="py-3 px-2">{vehicle.document}</td>
                        <td className="py-3 px-2">{vehicle.expiry}</td>
                        <td className="py-3 px-2">
                          <StatusBadge status={vehicle.status}>
                            {vehicle.status}
                          </StatusBadge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Fuel Consumption Analysis */}
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Fuel className="w-5 h-5 text-purple-600" />
                Fuel Consumption Analysis
              </h3>
              
              <div className="h-64 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={fuelConsumptionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="consumption" 
                      stroke="#8b5cf6" 
                      strokeWidth={3}
                      dot={{ fill: '#8b5cf6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Current Fuel Price: ${fuelPrice.toFixed(2)}/gallon</span>
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-4 h-4 text-red-500" />
                  <span className="text-red-500">+$0.12 from last week</span>
                </div>
              </div>
            </div>

            {/* Fuel Cost Breakdown */}
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-green-600" />
                Fuel Cost Breakdown
              </h3>
              
              <div className="h-64 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={fuelCostData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                    >
                      {fuelCostData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`$${value}`, 'Cost']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Weekly Fuel Expenses:</span>
                </div>
                <ProgressBar percentage={78} />
                <div className="text-sm text-gray-600">
                  $1,950 of $2,500 budget used (78%)
                </div>
              </div>
            </div>

            {/* Maintenance Schedule */}
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-orange-600" />
                Maintenance Schedule
              </h3>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Vehicle</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Next Service</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Hours/Miles</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {maintenanceData.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-2 font-medium">{item.vehicle}</td>
                        <td className="py-3 px-2">{item.nextService}</td>
                        <td className="py-3 px-2">{item.hours}</td>
                        <td className="py-3 px-2">
                          <StatusBadge status={item.priority}>
                            {item.priority}
                          </StatusBadge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Fleet Performance Overview */}
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Fleet Performance Overview
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-medium text-gray-700 mb-4">Vehicle Utilization Rate</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={utilizationData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="vehicle" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Bar dataKey="utilization" fill="#8b5cf6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-medium text-gray-700 mb-4">Overall Fleet Efficiency</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={efficiencyData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="subject" />
                      <PolarRadiusAxis domain={[0, 100]} />
                      <Radar
                        name="Fleet Performance"
                        dataKey="A"
                        stroke="#8b5cf6"
                        fill="#8b5cf6"
                        fillOpacity={0.3}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Critical Alerts */}
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Critical Alerts
              </h3>
              
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded animate-pulse">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                    <span className="font-semibold text-red-700">Critical:</span>
                    <span className="ml-2 text-red-600">CV-001 Excavator - Hydraulic leak detected</span>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                    <span className="font-semibold text-yellow-700">Warning:</span>
                    <span className="ml-2 text-yellow-600">CV-012 - Due for 500-hour service</span>
                  </div>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="font-semibold text-blue-700">Info:</span>
                    <span className="ml-2 text-blue-600">Fuel delivery scheduled for tomorrow</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Assignments */}
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-600" />
                Project Assignments
              </h3>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Project</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Vehicles</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Progress</th>
                      <th className="text-left py-3 px-2 font-semibold text-gray-700 text-sm">Est. Completion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectData.map((project, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-2 font-medium">{project.project}</td>
                        <td className="py-3 px-2">{project.vehicles} vehicles</td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <ProgressBar percentage={project.progress} className="flex-1" />
                            <span className="text-sm font-medium">{project.progress}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-2">{project.completion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ConstructionFleetDashboard;